import { useState } from 'react';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Switch } from '../ui/Switch';
import { Alert } from '../ui/Alert';
import { Modal } from '../ui/Modal';
import { NativeSelect } from '../ui/NativeSelect';
import { User, Shield, Cloud, CreditCard, Trash2, CheckCircle, AlertTriangle, Key, HardDrive, Unlink } from 'lucide-react';
import { authService } from '@/services/authService';
import { useSubscription } from '@/hooks/useSubscription';
import { ChangePasswordModal } from './ChangePasswordModal';
import { TwoFactorSetupModal } from './TwoFactorSetupModal';
import { simplifiedDriveService } from '@/services/googleDriveSimplified';
import { cloudSyncService } from '@/services/cloudSyncService';

export function AccountSettings() {
  const {
    user,
    isAuthenticated,
    updateSyncPreferences,
    setShowAuthModal,
    setAuthModalMode,
    signOut
  } = useAuthStore();

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDriveConnected, setIsDriveConnected] = useState(simplifiedDriveService.isConfigured());
  const { tier: hookTier, status: subStatus } = useSubscription();
  
  // Use hook tier, but also check auth store directly as a fallback
  const tier = hookTier === 'free' && (user?.subscription?.plan === 'pioneer' || user?.subscription?.isPioneer)
    ? 'pioneer'
    : hookTier;

  const handleSyncPreferenceChange = async (key: string, value: any) => {
    try {
      setSyncError(null);
      await updateSyncPreferences({ [key]: value });
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Failed to update sync preferences');
    }
  };

  if (!isAuthenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Account
          </CardTitle>
          <CardDescription>
            Sign in to unlock cloud features and cross-device sync
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start gap-3">
              <Cloud className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Why Create an Account?</h4>
                <ul className="text-sm text-blue-800 mt-2 space-y-1">
                  <li>• Sync your data across all devices</li>
                  <li>• Automatic cloud backups</li>
                  <li>• Access from anywhere</li>
                  <li>• Never lose your progress</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              className="flex-1"
              onClick={() => {
                setAuthModalMode('signin');
                setShowAuthModal(true);
              }}
            >
              Sign In
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setAuthModalMode('signup');
                setShowAuthModal(true);
              }}
            >
              Create Account
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Your current data will remain safe and will be migrated to your account automatically.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="text-primary-foreground text-lg font-semibold">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium">{user?.name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <p className="text-xs text-muted-foreground">
                Member since {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
          </div>

          {(subStatus || user?.subscription) && (
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-purple-600" />
                <span className="font-medium text-purple-900 capitalize">
                  {tier === 'pioneer' ? 'Pioneer' : (tier || user?.subscription?.plan || 'Free')} Plan
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  (subStatus?.status || user?.subscription?.status) === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {subStatus?.status || user?.subscription?.status || 'inactive'}
                </span>
              </div>
            </div>
          )}

          {!user?.emailVerified && (
            <Alert variant="warning">
              <AlertTriangle className="h-4 w-4" />
              <div>
                <p className="font-medium">Email not verified</p>
                <p className="text-sm">Check your email for a verification link.</p>
              </div>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Cloud Sync Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Cloud className="h-5 w-5 mr-2" />
            Cloud Sync Settings
          </CardTitle>
          <CardDescription>
            Manage how your data syncs across devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {syncError && (
            <Alert variant="destructive">
              {syncError}
            </Alert>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Enable Cloud Sync</p>
              <p className="text-sm text-muted-foreground">
                Automatically sync your data to the cloud
              </p>
            </div>
            <Switch
              checked={user?.preferences?.enableCloudSync || false}
              onCheckedChange={(checked) =>
                handleSyncPreferenceChange('enableCloudSync', checked)
              }
            />
          </div>

          {user?.preferences?.enableCloudSync && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Sync Frequency</label>
                <NativeSelect
                  value={user.preferences.syncInterval}
                  onChange={(e) =>
                    handleSyncPreferenceChange('syncInterval', e.target.value)
                  }
                >
                  <option value="realtime">Real-time (instant)</option>
                  <option value="hourly">Every hour</option>
                  <option value="daily">Daily</option>
                  <option value="manual">Manual only</option>
                </NativeSelect>
                <p className="text-xs text-muted-foreground">
                  Real-time syncing may use more battery on mobile devices
                </p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Encrypt Cloud Data</p>
                  <p className="text-sm text-muted-foreground">
                    Add extra security to your cloud data
                  </p>
                </div>
                <Switch
                  checked={user?.preferences?.encryptCloudData || false}
                  onCheckedChange={(checked) =>
                    handleSyncPreferenceChange('encryptCloudData', checked)
                  }
                />
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    Cloud sync is active - your data is automatically backed up
                  </span>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Google Drive Storage */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <HardDrive className="h-5 w-5 mr-2" />
            Cloud Storage
          </CardTitle>
          <CardDescription>
            Connect your Google Drive to sync data across devices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isDriveConnected ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Google Drive Connected</p>
                  <p className="text-sm text-muted-foreground">
                    Your data is automatically backed up
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  cloudSyncService.disconnect();
                  setIsDriveConnected(false);
                }}
              >
                <Unlink className="h-4 w-4 mr-2" />
                Disconnect
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Google Drive</p>
                <p className="text-sm text-muted-foreground">
                  Store backups in your personal Google Drive
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => simplifiedDriveService.connectDrive()}
              >
                Connect
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">
                Add an extra layer of security to your account
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowTwoFactor(true)}>
              {user?.preferences?.twoFactorEnabled ? 'Manage' : 'Enable'}
            </Button>
          </div>

          <hr />

          <Button
            variant="outline"
            className="w-full justify-center"
            onClick={() => setShowChangePassword(true)}
          >
            <Key className="h-4 w-4 mr-2" />
            Change Password
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Delete Account</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Account
            </Button>
          </div>
        </CardContent>
      </Card>

      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      <TwoFactorSetupModal
        isOpen={showTwoFactor}
        onClose={() => setShowTwoFactor(false)}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <Modal
          isOpen={true}
          onClose={() => setShowDeleteConfirm(false)}
          size="sm"
        >
          <Card className="border-0 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Delete Account</h3>
                  <p className="text-sm text-muted-foreground">
                    This action cannot be undone
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm">
                  Deleting your account will permanently remove:
                </p>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• All your aspirations, goals, and lists</li>
                  <li>• Your cloud sync data</li>
                  <li>• Your account information and preferences</li>
                  <li>• Any subscription or billing data</li>
                </ul>
                <p className="text-sm font-medium text-red-600">
                  This action is irreversible and cannot be undone.
                </p>
              </div>

              {deleteError && (
                <Alert variant="destructive" className="mb-4">
                  {deleteError}
                </Alert>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1"
                  disabled={isDeleting}
                  onClick={async () => {
                    setIsDeleting(true);
                    try {
                      await authService.deleteAccount();
                      setShowDeleteConfirm(false);
                      await signOut();
                    } catch (error) {
                      console.error('Account deletion failed:', error);
                      setDeleteError('Failed to delete account. Please try again.');
                    } finally {
                      setIsDeleting(false);
                    }
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </Modal>
      )}
    </div>
  );
}